// src/pages/payment/PaymentPage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/auth-context';
import Navbar from '../../components/marketing/Navbar';

const PaymentPage = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [lesson, setLesson] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  // Ref to prevent duplicate operations during redirect
  const isRedirectingRef = useRef(false);
  const hasFetchedRef = useRef(false);

  // CRITICAL: Validate lessonId immediately
  useEffect(() => {
    if (!lessonId) {
      console.error('❌ PaymentPage: No lessonId in URL params');
      console.error('Current URL:', window.location.href);
      console.error('useParams result:', { lessonId });
      
      setError('Geen geldige les-ID gevonden in de URL');
      setIsLoading(false);
      
      // Redirect to dashboard after showing error briefly
      setTimeout(() => {
        navigate('/dashboard', {
          state: {
            message: 'Ongeldige betalingspagina. Kies een training uit het dashboard.',
            type: 'error'
          }
        });
      }, 2000);
      
      return;
    }
    
    console.log('✅ PaymentPage mounted with lessonId:', lessonId);
  }, [lessonId, navigate]);

  useEffect(() => {
    // Don't fetch if we're redirecting or already fetched
    if (isRedirectingRef.current || hasFetchedRef.current) {
      console.log('Skipping fetch - redirecting or already fetched');
      return;
    }

    if (!token) {
      navigate('/login');
      return;
    }
    
    // Don't fetch if no lessonId
    if (!lessonId) {
      return;
    }
    
    fetchLessonDetails();
  }, [lessonId, token]);

  const fetchLessonDetails = async () => {
    // Double-check we're not redirecting
    if (isRedirectingRef.current) {
      console.log('⏭️ Skipping fetch - redirect in progress');
      return;
    }

    // Validate lessonId one more time
    if (!lessonId) {
      console.error('❌ fetchLessonDetails called without lessonId');
      return;
    }

    console.log('🔄 Fetching lesson details for:', lessonId);
    hasFetchedRef.current = true;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/lessons/${lessonId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Lesson details fetched:', data.lesson.title);
        setLesson(data.lesson);
        
        // If user already owns this lesson, redirect to dashboard
        if (data.lesson.isPurchased) {
          navigate('/dashboard', {
            state: {
              message: 'Je hebt deze training al gekocht!',
              type: 'info'
            }
          });
        }
      } else {
        console.error('❌ Failed to fetch lesson:', data.message);
        setError(data.message || 'Kon les details niet laden');
      }
    } catch (error) {
      console.error('❌ Fetch lesson error:', error);
      setError('Er is een fout opgetreden bij het laden van de les');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async () => {
    // Validate lessonId before creating payment
    if (!lessonId) {
      console.error('❌ handlePurchase: No lessonId available');
      setError('Kan betaling niet starten - geen geldige les-ID');
      return;
    }

    setError('');
    setIsCreatingPayment(true);

    console.log('💳 Creating payment for lessonId:', lessonId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/payments/create`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            lessonIds: [lessonId]
          })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        const paymentId = data.payment?.id || data.paymentId;
        console.log('✅ Payment created:', paymentId);
        
        // ✨ NEW: Store payment ID in localStorage before redirecting
        try {
          localStorage.setItem('ilearn_latest_payment_id', paymentId);
          console.log('💾 Payment ID stored in localStorage');
        } catch (storageError) {
          console.warn('⚠️ Could not store payment ID in localStorage:', storageError);
        }
        
        console.log('🔀 Redirecting to Mollie checkout...');
        
        // Set flags to prevent any further operations
        setIsRedirecting(true);
        isRedirectingRef.current = true;
        
        // Redirect to Mollie checkout
        // Use timeout to ensure state is set before redirect
        setTimeout(() => {
          const checkoutUrl = data.payment?.checkoutUrl || data.checkoutUrl;
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
          } else {
            console.error('❌ No checkout URL in response:', data);
            setError('Kon betalingslink niet ophalen. Probeer het opnieuw.');
            setIsCreatingPayment(false);
            setIsRedirecting(false);
            isRedirectingRef.current = false;
          }
        }, 100);
      } else {
        console.error('❌ Payment creation failed:', data.message);
        setError(data.message || 'Kon betaling niet aanmaken');
        setIsCreatingPayment(false);
      }
    } catch (error) {
      console.error('❌ Create payment error:', error);
      setError('Er is een fout opgetreden. Probeer het opnieuw.');
      setIsCreatingPayment(false);
    }
  };

  // Show error if no lessonId
  if (!lessonId) {
    return (
      <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
        <Navbar />
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <svg className="w-12 h-12 text-red-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-xl font-semibold text-red-900 mb-2">Ongeldige Betalingspagina</h2>
              <p className="text-red-700 mb-4">
                {error || 'Geen geldige les-ID gevonden. Je wordt doorgestuurd naar het dashboard...'}
              </p>
              <Link
                to="/dashboard"
                className="inline-block px-6 py-2 bg-purple-500 text-white font-semibold rounded-md hover:bg-purple-600 transition-colors"
              >
                Ga naar Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show redirecting state
  if (isRedirecting) {
    return (
      <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
        <Navbar />
        <div className="pt-24 pb-12 px-4 flex justify-center items-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-purple-800 mb-2">
              Doorsturen naar Betaling...
            </h2>
            <p className="text-gray-600">
              Je wordt doorgestuurd naar de beveiligde betaalpagina van Mollie.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
        <Navbar />
        <div className="pt-24 pb-12 px-4 flex justify-center items-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error && !lesson) {
    return (
      <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
        <Navbar />
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <svg className="w-12 h-12 text-red-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-xl font-semibold text-red-900 mb-2">Fout bij laden</h2>
              <p className="text-red-700 mb-4">{error}</p>
              <Link
                to="/dashboard"
                className="inline-block px-6 py-2 bg-purple-500 text-white font-semibold rounded-md hover:bg-purple-600 transition-colors"
              >
                Terug naar Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!lesson) return null;

  return (
    <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <div className="mb-6">
            <Link
              to="/dashboard"
              className="text-purple-600 hover:text-purple-800 font-medium inline-flex items-center"
            >
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Terug naar dashboard
            </Link>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-purple-600 text-white px-8 py-6">
              <h1 className="text-3xl font-light mb-2">{lesson.title}</h1>
              <p className="text-purple-100">Koop deze training en begin direct met leren</p>
            </div>

            {/* Content */}
            <div className="p-8">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
                  {error}
                </div>
              )}

              {/* Lesson Details */}
              <div className="mb-8">
                <h2 className="text-2xl font-light text-gray-800 mb-4">Training Details</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <svg className="w-6 h-6 text-purple-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="font-semibold text-gray-800">Duur</p>
                        <p className="text-gray-600">{lesson.duration} minuten ({Math.round(lesson.duration / 60)} uur)</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <svg className="w-6 h-6 text-purple-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="font-semibold text-gray-800">Modules</p>
                        <p className="text-gray-600">
                          {lesson.coreModules} kernmodules
                          {lesson.optionalModules > 0 && ` + ${lesson.optionalModules} optionele module(s)`}
                        </p>
                      </div>
                    </div>

                    {lesson.accreditation && (
                      <div className="flex items-start">
                        <svg className="w-6 h-6 text-purple-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                        <div>
                          <p className="font-semibold text-gray-800">Accreditatie</p>
                          <p className="text-gray-600">{lesson.accreditation.points} punten ({lesson.accreditation.provider})</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - What You'll Learn */}
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Wat je leert:</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">Interactieve gesprekssimulaties</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">Persoonlijke feedback op je gesprekstechnieken</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">Oefenen in je eigen tempo</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">Directe toegang na aankoop</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">Levenslange toegang tot de training</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Price and Purchase */}
              <div className="border-t border-gray-200 pt-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="mb-6 md:mb-0">
                    <p className="text-gray-600 mb-1">Prijs</p>
                    <p className="text-4xl font-semibold text-purple-800">
                      €{(lesson.price * 1.21).toFixed(2).replace('.', ',')}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      €{lesson.price.toFixed(2).replace('.', ',')} excl. BTW
                    </p>
                    <p className="text-sm text-gray-500 mt-1">Eenmalige betaling, geen abonnement</p>
                  </div>

                  <button
                    onClick={handlePurchase}
                    disabled={isCreatingPayment || isRedirecting}
                    className={`w-full md:w-auto px-8 py-4 text-lg font-semibold rounded-lg transition-all ${
                      isCreatingPayment || isRedirecting
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {isCreatingPayment || isRedirecting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Betaling voorbereiden...
                      </span>
                    ) : (
                      'Koop Nu en Begin Met Leren'
                    )}
                  </button>
                </div>
              </div>

              {/* Payment Info */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-start text-sm text-gray-600">
                  <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-800 mb-1">Veilige betaling via Mollie</p>
                    <p>Je wordt doorgestuurd naar een beveiligde betaalpagina. We accepteren iDEAL, creditcard en andere betaalmethoden.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-center text-sm text-gray-600">
            <p>
              Vragen over deze training?{' '}
              <a href="#" className="text-purple-600 hover:text-purple-800 font-medium">
                Neem contact met ons op
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;